import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  AlertCircle,
  AlertTriangle,
  Box,
  CheckCircle,
  Eye,
  EyeOff,
  LayoutGrid,
  MapPin,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { advisorAccessFor } from '../../utils/advisor';
import {
  applyUpdates,
  clearLensStock,
  createLensTransferRequestBatch,
  previewOrderExcel,
  getMatrix,
  getStockIntGroups,
  importExcel,
  releaseReservedCell,
  returnLensToCentral,
} from '../../services/stockService';

import {
  LENS_SPH_VALUES as DEFAULT_SPH_VALUES,
  LENS_CYL_VALUES as DEFAULT_CYL_VALUES,
  LENS_CELL_W as CELL_W,
  LENS_CELL_H as CELL_H,
  LENS_SPH_COL_W as SPH_COL_W,
  formatDecimalKey,
  formatCylLabel,
  zoneBg,
} from '../../utils/lensGrid';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;


const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 2rem;
  animation: ${fadeIn} 0.25s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: 1.8rem;
  font-weight: 800;
`;

const PageSubtitle = styled.p`
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const HeroPanel = styled.section`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 58%, #334155 100%);
  border-radius: 20px;
  padding: 1.1rem;
  color: white;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1.15fr) minmax(220px, 0.85fr) auto;
  gap: 0.85rem;
  align-items: end;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const HeroLabel = styled.label`
  color: #cbd5e1;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroSelect = styled.select`
  min-height: 48px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 0 0.85rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  outline: none;

  option { color: #0f172a; }
  &:focus {
    border-color: #a5b4fc;
    box-shadow: 0 0 0 4px rgba(165, 180, 252, 0.18);
  }
`;

const Button = styled.button`
  min-height: 42px;
  border: 1px solid ${props => props.$variant === 'danger' ? '#fecaca' : props.$variant === 'primary' ? '#4f46e5' : '#e2e8f0'};
  border-radius: 10px;
  padding: 0 0.85rem;
  background: ${props => {
    if (props.$variant === 'danger') return '#fff1f2';
    if (props.$variant === 'primary') return '#4f46e5';
    return 'white';
  }};
  color: ${props => {
    if (props.$variant === 'danger') return '#b91c1c';
    if (props.$variant === 'primary') return 'white';
    return '#475569';
  }};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-weight: 750;
  font-size: 0.84rem;
  transition: 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const HeroButton = styled(Button)`
  min-height: 48px;
  border-color: rgba(255, 255, 255, 0.2);
  background: #facc15;
  color: #111827;
`;

const LocationBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 0.8rem 1rem;
  display: flex;
  gap: 0.65rem;
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  min-height: 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  color: #334155;
  padding: 0 0.75rem;
  font-weight: 650;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const CurrentLocation = styled.div`
  min-height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  color: #475569;
  padding: 0 0.75rem;
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  font-weight: 650;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 0.75rem;
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const StatIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 850;
`;

const Notice = styled.div`
  border-radius: 12px;
  border: 1px solid ${props => props.$type === 'error' ? '#fecaca' : '#bbf7d0'};
  background: ${props => props.$type === 'error' ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.$type === 'error' ? '#b91c1c' : '#15803d'};
  padding: 0.7rem 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 650;

  button {
    margin-left: auto;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: inherit;
    display: flex;
  }
`;

// Hugs its tables rather than filling a fixed grid track. A track wide enough
// for two panes leaves that much dead white space inside a card holding one.
const MatrixCard = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  flex: 0 1 auto;
  max-width: 100%;
`;

const MatrixHeader = styled.div`
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const MatrixTitle = styled.h2`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  min-height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0 0.65rem;

  input {
    width: 80px;
    border: 0;
    background: transparent;
    outline: none;
    color: #334155;
    font-weight: 650;
  }
`;

// Hugs the table instead of filling its column, so the scrollbar sits flush
// against the last CYL column rather than across a strip of empty space.
const TableWrapper = styled.div`
  overflow: auto;
  max-height: 74vh;
  width: max-content;
  max-width: 100%;
`;

const qtyColor = (qty) => {
  if (qty === 0) return '#94a3b8';
  if (qty <= 2) return '#b45309';
  if (qty <= 5) return '#92400e';
  return '#111827';
};

// Pinned to its natural width rather than width:100%. Stretching to fill the
// container turns each cell into a thin rectangle with a lone digit lost in the
// middle of it (86px wide against a 17px height on a 1600px screen). Narrower
// viewports overflow and TableWrapper scrolls, which the min-width is for.
const MATRIX_W = SPH_COL_W + (CELL_W * 17);

// PairLayout's horizontal padding, and the resulting natural width of a card
// holding a single matrix. Both the card grid and the pane grid size off these
// so a card hugs its table instead of being floored at a stale literal — that
// mismatch is what leaves a strip of white space to the right of the grid.
const PAIR_PAD = 12;
const PAIR_GAP = 12;

// Crosshair. Hovering a cell lights its CYL column header, its SPH row header,
// and a faint band down the column and across the row — in a 49×17 grid you
// otherwise have to count cells to know which power you are on.
//
// Deliberately pure CSS via :has() rather than React hover state: tracking the
// hovered cell in state would re-render all 833 styled cells on every mouse
// move and stutter. This costs nothing at runtime, and on a browser without
// :has() the column half simply doesn't highlight — the row half still does.
const HL_BAND = 'rgba(79, 70, 229, 0.10)';
const CROSSHAIR = DEFAULT_CYL_VALUES.map((_, i) => `
  &:has(tbody td.col-${i}:hover) thead th.col-${i} {
    background: #fde68a;
    color: #111827;
  }
  &:has(tbody td.col-${i}:hover) tbody td.col-${i}::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${HL_BAND};
    pointer-events: none;
  }
`).join('');

const StockTable = styled.table`
  min-width: ${MATRIX_W}px;
  width: ${MATRIX_W}px;
  border-collapse: separate;
  border-spacing: 0;

  th, td {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 0;
    text-align: center;
  }

  /* The order pad's cells are plain <td>; the stock pane's CellShell already
     sets this. Both need it to anchor the highlight overlay. */
  tbody td {
    position: relative;
  }

  tbody tr:hover th {
    box-shadow: inset 0 0 0 2px #4f46e5;
  }

  tbody tr:hover td::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${HL_BAND};
    pointer-events: none;
  }

  ${CROSSHAIR}

  thead th {
    position: sticky;
    z-index: 4;
    background: #fefce8;
    color: #334155;
    height: ${CELL_H}px;
    font-size: 0.6rem;
    font-weight: 800;
  }

  /* Each header row pins directly below the one above it. Without the stagger
     all three share top:0 and collapse onto each other as the body scrolls. */
  thead tr:nth-child(1) th { top: 0; }
  thead tr:nth-child(2) th { top: ${CELL_H}px; }
  thead tr:nth-child(3) th { top: ${CELL_H * 2}px; }

  thead tr:first-child th {
    background: #facc15;
    color: #111827;
    border-bottom: 2px solid #22c55e;
    font-size: 0.72rem;
  }

  /* SPH holds the corner across the CYL label and number rows, and pins left
     too so it survives a sideways scroll like the SPH values below it. */
  thead tr:nth-child(2) th:first-child {
    left: 0;
    z-index: 5;
    width: ${SPH_COL_W}px;
    background: #fde68a;
    color: #111827;
    letter-spacing: 0.04em;
  }

  thead tr:nth-child(2) th:last-child {
    background: #fef9c3;
    color: #111827;
    letter-spacing: 0.16em;
  }

  tbody th {
    position: sticky;
    left: 0;
    z-index: 3;
    width: ${SPH_COL_W}px;
    background: ${props => props.$unused || '#ef4444'};
    color: white;
    font-weight: 850;
    font-size: 0.62rem;
  }
`;

// The per-cell Order / Return actions used to sit stacked UNDER the value,
// which tripled every row's height across 1100 cells. They now overlay the
// bottom of the cell on hover / keyboard focus, costing no height at all.
const CellShell = styled.td`
  position: relative;

  &:hover .cell-actions,
  &:focus-within .cell-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

const CellActions = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 1px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease;
  z-index: 3;
`;

const MiniBtn = styled.button`
  flex: 1;
  min-width: 0;
  border: 0;
  padding: 2px 0;
  font-size: 0.55rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
  cursor: ${props => (props.disabled ? 'wait' : 'pointer')};
  background: ${props => (props.$tone === 'return' ? '#ffedd5' : '#e0e7ff')};
  color: ${props => (props.$tone === 'return' ? '#c2410c' : '#4338ca')};

  &:hover:not(:disabled) { filter: brightness(0.94); }
`;

const SphHeader = styled.th`
  background: ${props => {
    if (props.$value > 0) return '#22c55e';
    if (props.$value === 0) return '#94a3b8';
    return '#ef4444';
  }} !important;
  border-right-color: ${props => {
    if (props.$value > 0) return '#15803d';
    if (props.$value === 0) return '#64748b';
    return '#991b1b';
  }} !important;
`;

const CellButton = styled.button`
  min-width: ${CELL_W}px;
  min-height: ${CELL_H}px;
  width: 100%;
  border: 0;
  font-size: 0.64rem;
  background: ${props => props.$bg};
  color: ${props => props.$pending ? '#312e81' : qtyColor(props.$qty)};
  font-weight: ${props => props.$pending || props.$qty > 0 ? 800 : 550};
  cursor: ${props => props.$readOnly ? 'default' : 'pointer'};
  transition: 0.1s ease;
  box-shadow: ${props => {
    if (props.$pending) return 'inset 0 0 0 2px #6366f1';
    if (props.$reserved) return 'inset 0 -3px 0 #f59e0b';
    return 'none';
  }};

  &:hover {
    ${props => !props.$readOnly && css`
      transform: scale(1.05);
      box-shadow: inset 0 0 0 2px #4f46e5;
      z-index: 2;
      position: relative;
    `}
  }
`;

const CellInput = styled.input`
  min-width: ${CELL_W}px;
  min-height: ${CELL_H}px;
  width: 100%;
  border: 2px solid #4f46e5;
  text-align: center;
  font-size: 0.64rem;
  font-weight: 850;
  color: #111827;
  outline: none;
`;

const Legend = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.7rem 1rem;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 650;
`;

const Dot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
  background: ${props => props.$bg};
  border: 1px solid ${props => props.$border || 'transparent'};
  vertical-align: middle;
  margin-right: 0.35rem;
`;

const ImportPanel = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  display: ${props => props.$open ? 'block' : 'none'};
`;

const ImportGrid = styled.div`
  display: grid;
  grid-template-columns: 180px minmax(220px, 1fr) auto;
  gap: 0.75rem;
  align-items: end;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  min-height: 40px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 0.75rem;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: #64748b;

  strong {
    display: block;
    color: #0f172a;
    margin-bottom: 0.35rem;
  }
`;

// Two lenses side by side on a wide screen, stacking to one when there isn't
// room. Four products therefore read as 2 on top and 2 below.
const MatrixGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
  justify-content: flex-start;
`;

// Stock on the left, the order pad on the right. Both are the same SPH/CYL
// grid so a row lines up across the pair — you read what you have and type what
// you need without moving your eye off the power.
// Track MATRIX_W rather than a hardcoded width — a stale literal here leaves a
// strip of dead space between the table and its scrollbar when the cells shrink.
const PairLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${PAIR_GAP}px;
  align-items: flex-start;
  padding: 0 ${PAIR_PAD}px ${PAIR_PAD}px;
  justify-content: flex-start;
`;

const PairPane = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
`;

const PaneTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => (p.$tone === 'order' ? '#4338ca' : '#0f766e')};
  padding: 0.4rem 0 0.35rem;
`;

// Send order sits on the pane it acts on, not up in the card header — the
// button and the cells it sends belong to the same table.
const PaneTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-height: 34px;
`;

// Ordered cells read as a request, not as stock, so they get their own colour
// rather than looking like quantities that are already on the shelf.
const OrderCellButton = styled(CellButton)`
  background: ${p => (p.$qty > 0 ? '#e0e7ff' : '#fff')};
  color: ${p => (p.$qty > 0 ? '#3730a3' : '#cbd5e1')};
  box-shadow: ${p => (p.$qty > 0 ? 'inset 0 0 0 1.5px #6366f1' : 'none')};
`;

// ─── One lens's matrix ───────────────────────────────────────────────────────
//
// Extracted so several lenses can sit side by side. Each card owns everything
// scoped to its own lens — the matrix, pending edits, saving, and the
// order/return dialogs — while the page keeps what is shared: the lens list,
// the location, imports and notices.
//
// `group` is a Stock Int catalog group (see the server's stockIntLensGroups):
// it carries the display label and the lens_products row that holds the stock.
const ProductMatrix = ({
  group,
  locationType,
  locationId,
  canEditMatrix,
  isReadOnly,
  isStoreManager,
  canOrderFromCentral,
  showStockPane,
  sphFilter,
  showNotice,
  onStats,
  refreshKey,
}) => {
  const { t } = useTranslation();
  const product = group.product || {};
  const productId = product._id || product.id;
  const productName = group.label || product.name || 'Lens';

  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [edits, setEdits] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearingStock, setClearingStock] = useState(false);

  // Quantities typed into the order pad, keyed sph:cyl — kept apart from
  // `edits` so a pending stock correction is never confused with a request.
  const [orderEdits, setOrderEdits] = useState({});
  const [orderEditingKey, setOrderEditingKey] = useState(null);
  const [orderEditingValue, setOrderEditingValue] = useState('');
  const [sendingOrder, setSendingOrder] = useState(false);
  const [orderImporting, setOrderImporting] = useState(false);
  const orderFileRef = useRef(null);

  const editCount = Object.keys(edits).length;
  const orderCount = Object.values(orderEdits).filter((q) => Number(q) > 0).length;


  const reloadMatrix = useCallback(async () => {
    if (!productId || !locationId) {
      setMatrix(null);
      return;
    }
    setMatrixLoading(true);
    try {
      const data = await getMatrix({ productId, location: { type: locationType, id: locationId } });
      setMatrix(data);
      setEdits({});
    } catch (error) {
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      showNotice('error', serverMessage || `Failed to load ${productName} matrix`);
    } finally {
      setMatrixLoading(false);
    }
  }, [productId, locationId, locationType, productName, showNotice]);

  // Also on refreshKey: reloading the lens list alone left every matrix showing
  // the quantities it had when the card first mounted.
  useEffect(() => { reloadMatrix(); }, [reloadMatrix, refreshKey]);

  // Exactly SPH -6…+6 and CYL 0…4, always. These used to be unioned with the
  // powers the saved matrix happened to contain, which is what put half-height
  // blank stripes between the rows: any stored power off the 0.25 grid (or left
  // over from the old -8…+8 range) became a row of its own.
  const visibleSphValues = useMemo(() => {
    if (!sphFilter.trim()) return DEFAULT_SPH_VALUES;
    return DEFAULT_SPH_VALUES.filter((sph) => formatDecimalKey(sph).includes(sphFilter.trim()));
  }, [sphFilter]);

  const visibleCylValues = DEFAULT_CYL_VALUES;

  // Fixing the axes means stock sitting outside them would silently vanish, so
  // count it and say so rather than letting it disappear off the grid.
  const offGridUnits = useMemo(() => {
    const inSph = new Set(DEFAULT_SPH_VALUES.map(formatDecimalKey));
    const inCyl = new Set(DEFAULT_CYL_VALUES.map(formatDecimalKey));
    let units = 0;
    Object.entries(matrix?.cells || {}).forEach(([sphKey, row]) => {
      Object.entries(row || {}).forEach(([cylKey, qty]) => {
        if (inSph.has(sphKey) && inCyl.has(cylKey)) return;
        units += Number(qty) || 0;
      });
    });
    return units;
  }, [matrix?.cells]);

  // Report this product's totals up so the page shows one combined set of stat
  // cards rather than a row of numbers per lens.
  useEffect(() => {
    let available = 0; let reserved = 0; let low = 0; let filled = 0;
    Object.values(matrix?.cells || {}).forEach((row) => {
      Object.values(row || {}).forEach((qty) => {
        const n = Number(qty) || 0;
        available += n;
        if (n > 0) { filled += 1; if (n <= 2) low += 1; }
      });
    });
    Object.values(matrix?.reservedCells || {}).forEach((row) => {
      Object.values(row || {}).forEach((qty) => { reserved += Number(qty) || 0; });
    });
    onStats(productId, { available, reserved, low, filled });
  }, [matrix, productId, onStats]);

  const handleCellClick = (sphKey, cylKey, value) => {
    if (!canEditMatrix) return;
    const key = `${sphKey}:${cylKey}`;
    setEditingKey(key);
    setEditingValue(String(edits[key] ?? (value ?? 0)));
  };

  const commitEdit = () => {
    if (!editingKey) return;
    const parsed = Number.parseInt(editingValue, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      showNotice('error', 'Quantity must be 0 or more');
      return;
    }
    setEdits((prev) => ({ ...prev, [editingKey]: parsed }));
    setEditingKey(null);
    setEditingValue('');
  };

  const handleSave = async () => {
    const entries = Object.entries(edits);
    if (!entries.length) return;
    const updates = entries.map(([key, qty]) => {
      const [sph, cyl] = key.split(':');
      const reservedQty = matrix?.reservedCells?.[sph]?.[cyl] ?? 0;
      return { sph: Number(sph), cyl: Number(cyl), qty: qty + reservedQty };
    });
    setSaving(true);
    try {
      await applyUpdates({ productId, location: { type: locationType, id: locationId }, updates });
      showNotice('success', `${updates.length} cell(s) saved for ${productName}`);
      await reloadMatrix();
    } catch (error) {
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      showNotice('error', serverMessage || 'Failed to save matrix');
    } finally {
      setSaving(false);
    }
  };

  const handleClearStock = async () => {
    if (!window.confirm(`Clear all available stock for "${productName}"? Reserved stock will stay protected.`)) return;
    setClearingStock(true);
    try {
      await clearLensStock({ productId, location: { type: locationType, id: locationId } });
      showNotice('success', `Cleared available stock for ${productName}`);
      await reloadMatrix();
    } catch (error) {
      const serverMessage = error.response?.data?.error || error.response?.data?.message;
      showNotice('error', serverMessage || 'Failed to clear stock');
    } finally {
      setClearingStock(false);
    }
  };

  const commitOrderEdit = () => {
    if (!orderEditingKey) return;
    const parsed = Number.parseInt(orderEditingValue, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      showNotice('error', 'Order quantity must be 0 or more');
      return;
    }
    setOrderEdits((prev) => {
      const next = { ...prev };
      if (parsed > 0) next[orderEditingKey] = parsed;
      else delete next[orderEditingKey];   // 0 means "don't order this one"
      return next;
    });
    setOrderEditingKey(null);
    setOrderEditingValue('');
  };

  // Fill the pad from a stock sheet. The file is parsed server-side and comes
  // back as cells — nothing is ordered here. The numbers land on the grid, the
  // store checks them, and Send order is still the only thing that commits.
  //
  // Imported quantities replace whatever is on the pad rather than adding to
  // it: importing the same sheet twice should give the same order, not double
  // one, and anything typed by hand for a power the sheet also names would
  // otherwise silently compound.
  const handleOrderImport = async (file) => {
    if (!file) return;
    setOrderImporting(true);
    try {
      const result = await previewOrderExcel({ file });

      // Only powers the grid actually draws. A cell off the SPH -6…+6 / CYL 0…4
      // axes cannot be seen or corrected on the pad, so importing one would put
      // units into an order nobody could check — the thing this flow exists to
      // let them do.
      const inSph = new Set(DEFAULT_SPH_VALUES.map(formatDecimalKey));
      const inCyl = new Set(DEFAULT_CYL_VALUES.map(formatDecimalKey));
      const next = {};
      let offGrid = 0;
      let offGridUnits = 0;
      for (const cell of result.cells || []) {
        const sphKey = formatDecimalKey(cell.sph);
        const cylKey = formatDecimalKey(cell.cyl);
        if (!inSph.has(sphKey) || !inCyl.has(cylKey)) {
          offGrid += 1;
          offGridUnits += Number(cell.qty) || 0;
          continue;
        }
        next[`${sphKey}:${cylKey}`] = Number(cell.qty);
      }

      setOrderEdits(next);

      const cellCount = Object.keys(next).length;
      const units = Object.values(next).reduce((sum, q) => sum + q, 0);
      if (!cellCount) {
        showNotice('error', offGrid
          ? `Nothing to order: all ${offGrid} correction(s) in the sheet sit outside the SPH -6…+6 / CYL 0…4 grid.`
          : 'That sheet has no quantities above zero.');
        return;
      }

      // The sheet names its lens in its first cell. Loading one lens's sheet
      // into another lens's pad is a mistake worth naming out loud, but not one
      // worth refusing — the numbers are on screen and still have to be sent.
      const sheetName = String(result.productName || '').trim();
      const mismatch = sheetName && result.matchedProductId
        && String(result.matchedProductId) !== String(productId);
      const extras = [
        offGrid > 0 && `${offGrid} off-grid correction(s) skipped (${offGridUnits} unit(s))`,
        result.skippedZero > 0 && `${result.skippedZero} empty cell(s) ignored`,
        result.errors?.length > 0 && `${result.errors.length} bad row(s) skipped`,
      ].filter(Boolean).join(' · ');

      showNotice(
        mismatch ? 'error' : 'success',
        mismatch
          ? `Loaded ${cellCount} correction(s), ${units} unit(s) — but that sheet is for "${sheetName}", not ${productName}. Check before sending.`
          : `Loaded ${cellCount} correction(s), ${units} unit(s) into the ${productName} pad.${extras ? ` ${extras}.` : ''} Check the table, then press Send order.`
      );
    } catch (error) {
      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      showNotice('error', serverMessage || 'Could not read that Excel file.');
    } finally {
      setOrderImporting(false);
      // Clear the input so picking the same file again still fires onChange.
      if (orderFileRef.current) orderFileRef.current.value = '';
    }
  };

  // The whole pad goes in one request. It used to be one HTTP call per SPH/CYL,
  // awaited in sequence — a full-matrix order meant hundreds of round trips and
  // minutes on "Sending…", with central notified once per correction.
  const sendOrder = async () => {
    const entries = Object.entries(orderEdits).filter(([, qty]) => Number(qty) > 0);
    if (!entries.length) return;
    const cells = entries.map(([key, qty]) => {
      const [sph, cyl] = key.split(':');
      return { key, sph: Number(sph), cyl: Number(cyl), qty: Number(qty) };
    });

    setSendingOrder(true);
    try {
      const result = await createLensTransferRequestBatch({
        lensProductId: productId,
        cells: cells.map(({ sph, cyl, qty }) => ({ sph, cyl, qty })),
      });
      setOrderEdits({});
      // Central fills what it can off its own shelf and orders the rest. Saying
      // which is which is the whole point of the summary.
      const readyUnits = Number(result?.readyQty) || 0;
      const waitingUnits = Number(result?.pendingQty) || 0;
      const split = [
        readyUnits > 0 && `${readyUnits} ready to transfer from central stock`,
        waitingUnits > 0 && `${waitingUnits} waiting for supplier`,
      ].filter(Boolean).join(' · ');
      showNotice('success', `Ordered ${cells.length} cell(s) of ${productName}.${split ? ` ${split}.` : ''}`);
    } catch (error) {
      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      showNotice('error', serverMessage || `Could not order ${productName} from central.`);
    } finally {
      setSendingOrder(false);
    }
    await reloadMatrix();
  };

  // Reserved units are held out of available stock. When the thing that held
  // them is gone, this is what puts them back on the shelf.
  const handleReleaseReserved = async (sphKey, cylKey, reservedQty) => {
    if (reservedQty <= 0) return;
    const rawQty = window.prompt(
      `Release how many reserved ${productName} SPH ${Number(sphKey)} / CYL ${Number(cylKey)} back into stock?`,
      String(reservedQty)
    );
    if (rawQty === null) return;
    const qty = Math.max(1, Math.min(reservedQty, parseInt(rawQty, 10) || 0));
    try {
      await releaseReservedCell({
        lensProductId: productId,
        locationId,
        sph: Number(sphKey),
        cyl: Number(cylKey),
        qty,
      });
      showNotice('success', `Released ${qty} unit(s) back into stock.`);
      await reloadMatrix();
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to release reserved stock');
    }
  };

  const handleReturnLensToCentral = async (sphKey, cylKey, availableQty) => {
    if (availableQty <= 0) return;
    const rawQty = window.prompt(`Return how many ${productName} SPH ${Number(sphKey)} / CYL ${Number(cylKey)} to central?`, '1');
    if (rawQty === null) return;
    const qty = Math.max(1, parseInt(rawQty, 10) || 1);
    try {
      await returnLensToCentral({ lensProductId: productId, sph: Number(sphKey), cyl: Number(cylKey), qty });
      showNotice('success', `Returned ${qty} unit(s) to central.`);
      await reloadMatrix();
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to return lens to central');
    }
  };

  return (
    <MatrixCard>
      <MatrixHeader>
        <div>
          <MatrixTitle>{productName}</MatrixTitle>
          {/* The grid dimensions and the catalog groups are both readable off
              the table itself, so the subtitle stays empty in the normal case.
              Off-grid units are the exception: that stock exists but has no
              cell to appear in, so suppressing the warning would hide it. */}
          {(matrixLoading || (showStockPane && offGridUnits > 0)) && (
            <PageSubtitle style={{ fontSize: '0.8rem' }}>
              {matrixLoading ? t('lensStock.loading') : (
                <span style={{ color: '#b45309', fontWeight: 700 }}>
                  {offGridUnits} {t('lensStock.unitSOutsideSph6')}
                </span>
              )}
            </PageSubtitle>
          )}
        </div>
        <Toolbar>
          <Button onClick={reloadMatrix} disabled={matrixLoading} title={t('lensStock.refreshThisLens')}>
            <RefreshCw size={14} />
          </Button>
          {canEditMatrix && (
            <>
              {/* No delete: the lens list mirrors the Stock Int catalog, so a
                  deleted card would reappear on the next load. To take a lens
                  off this page, unmark it in Admin → Lens Catalog. */}
              <Button $variant="danger" onClick={handleClearStock} disabled={clearingStock || isReadOnly} title={t('lensStock.clearAvailableStock')}>
                <Trash2 size={14} />
              </Button>
            </>
          )}
          {canEditMatrix && editCount > 0 && (
            <>
              <Button onClick={() => setEdits({})}><X size={14} /> {t('lensStock.discard')}</Button>
              <Button $variant="primary" onClick={handleSave} disabled={saving}>
                <Save size={14} />
                {saving ? t('lensStock.saving') : `Save (${editCount})`}
              </Button>
            </>
          )}
        </Toolbar>
      </MatrixHeader>

      <PairLayout>
        {/* The store's own shelf. Hidden in the order-only view, where the
            store gets the same Stock Int tables central holds but with no
            quantities on them — just cells to fill with what it needs. */}
        {showStockPane && (
        <PairPane>
          {/* Same wrapper as the order pane even with nothing beside it: the
              two panes sit side by side, so an uneven title strip would offset
              the rows the pair layout exists to line up. */}
          <PaneTitleRow>
            <PaneTitle>{t('lensStock.stockAvailable')}</PaneTitle>
          </PaneTitleRow>
          <TableWrapper>
            <StockTable>
          <thead>
            <tr>
              <th colSpan={visibleCylValues.length + 1}>{productName}</th>
            </tr>
            {/* CYL spans its own columns under the lens name; SPH holds the
                corner and drops down over the number row beside it. */}
            <tr>
              <th rowSpan={2}>{t('lensStock.sph')}</th>
              <th colSpan={visibleCylValues.length}>{t('lensStock.cyl')}</th>
            </tr>
            <tr>
              {visibleCylValues.map((cyl, cylIndex) => (
                <th key={formatDecimalKey(cyl)} className={`col-${cylIndex}`}>{formatCylLabel(cyl)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleSphValues.map((sph) => {
              const sphKey = formatDecimalKey(sph);
              return (
                <tr key={sphKey}>
                  <SphHeader $value={Number(sph)}>{Number(sph).toString()}</SphHeader>
                  {visibleCylValues.map((cyl, cylIndex) => {
                    const cylKey = formatDecimalKey(cyl);
                    const key = `${sphKey}:${cylKey}`;
                    const currentValue = matrix?.cells?.[sphKey]?.[cylKey] ?? 0;
                    const reservedValue = matrix?.reservedCells?.[sphKey]?.[cylKey] ?? 0;
                    const pendingValue = edits[key];
                    const displayValue = pendingValue !== undefined ? pendingValue : currentValue;
                    const bg = pendingValue !== undefined ? '#e0e7ff' : zoneBg(sph, cyl);
                    return (
                      <CellShell key={key} className={`col-${cylIndex}`}>
                        {editingKey === key ? (
                          <CellInput
                            type="number"
                            min="0"
                            value={editingValue}
                            onChange={(event) => setEditingValue(event.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') commitEdit();
                              if (event.key === 'Escape') { setEditingKey(null); setEditingValue(''); }
                            }}
                            autoFocus
                          />
                        ) : (
                          <CellButton
                            type="button"
                            $bg={bg}
                            $qty={displayValue}
                            $pending={pendingValue !== undefined}
                            $reserved={reservedValue > 0}
                            $readOnly={isReadOnly}
                            onClick={() => handleCellClick(sphKey, cylKey, displayValue)}
                            title={`SPH ${sphKey} / CYL ${formatCylLabel(cyl)}: ${displayValue} available${reservedValue ? `, ${reservedValue} reserved` : ''}`}
                          >
                            {displayValue || ''}
                          </CellButton>
                        )}
                        {editingKey !== key && (currentValue > 0 || reservedValue > 0) && (
                          <CellActions className="cell-actions">
                            {isStoreManager && currentValue > 0 && (
                              <MiniBtn type="button" $tone="return"
                                onClick={() => handleReturnLensToCentral(sphKey, cylKey, currentValue)}
                                title={t('lensStock.returnThisSphCylStock')}>
                                {t('lensStock.return')}
                              </MiniBtn>
                            )}
                            {reservedValue > 0 && (
                              <MiniBtn type="button"
                                onClick={() => handleReleaseReserved(sphKey, cylKey, reservedValue)}
                                title={`${reservedValue} unit(s) reserved here — put them back into available stock`}>
                                {t('lensStock.release')} {reservedValue}
                              </MiniBtn>
                            )}
                          </CellActions>
                        )}
                      </CellShell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
            </StockTable>
          </TableWrapper>
        </PairPane>
        )}

        {/* Order pad — same grid, but the numbers are what you want sent, not
            what you hold. Nothing leaves until Send order is pressed. */}
        {canOrderFromCentral && (
          <PairPane>
            <PaneTitleRow>
              <PaneTitle $tone="order">{t('lensStock.orderFromCentral')}</PaneTitle>
              {/* Import stays visible on an empty pad — filling a blank order
                  from a sheet is the whole point of it. */}
              <Toolbar>
                <input
                  ref={orderFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={(event) => handleOrderImport(event.target.files?.[0] || null)}
                />
                <Button
                  onClick={() => orderFileRef.current?.click()}
                  disabled={orderImporting || sendingOrder}
                >
                  <Upload size={14} />
                  {orderImporting ? t('lensStock.reading') : t('lensStock.importExcel')}
                </Button>
                {orderCount > 0 && (
                  <>
                    <Button onClick={() => setOrderEdits({})}><X size={14} /> {t('lensStock.clearOrder')}</Button>
                    <Button $variant="primary" onClick={sendOrder} disabled={sendingOrder}>
                      <Save size={14} />
                      {sendingOrder ? t('lensStock.sending') : `Send order (${orderCount})`}
                    </Button>
                  </>
                )}
              </Toolbar>
            </PaneTitleRow>
            <TableWrapper>
              <StockTable>
                <thead>
                  <tr>
                    <th colSpan={visibleCylValues.length + 1}>{t('lensStock.order')} {productName}</th>
                  </tr>
                  {/* Same three-row head as the stock pane — the two tables sit
                      side by side, so an uneven header would break the row
                      alignment the pair exists for. */}
                  <tr>
                    <th rowSpan={2}>{t('lensStock.sph')}</th>
                    <th colSpan={visibleCylValues.length}>{t('lensStock.cyl')}</th>
                  </tr>
                  <tr>
                    {visibleCylValues.map((cyl, cylIndex) => (
                      <th key={formatDecimalKey(cyl)} className={`col-${cylIndex}`}>{formatCylLabel(cyl)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleSphValues.map((sph) => {
                    const sphKey = formatDecimalKey(sph);
                    return (
                      <tr key={sphKey}>
                        <SphHeader $value={Number(sph)}>{Number(sph).toString()}</SphHeader>
                        {visibleCylValues.map((cyl, cylIndex) => {
                          const cylKey = formatDecimalKey(cyl);
                          const key = `${sphKey}:${cylKey}`;
                          const wanted = orderEdits[key] ?? 0;
                          const inStock = matrix?.cells?.[sphKey]?.[cylKey] ?? 0;
                          return (
                            <td key={key} className={`col-${cylIndex}`}>
                              {orderEditingKey === key ? (
                                <CellInput
                                  type="number"
                                  min="0"
                                  value={orderEditingValue}
                                  onChange={(event) => setOrderEditingValue(event.target.value)}
                                  onBlur={commitOrderEdit}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') commitOrderEdit();
                                    if (event.key === 'Escape') { setOrderEditingKey(null); setOrderEditingValue(''); }
                                  }}
                                  autoFocus
                                />
                              ) : (
                                <OrderCellButton
                                  type="button"
                                  $qty={wanted}
                                  onClick={() => {
                                    setOrderEditingKey(key);
                                    setOrderEditingValue(String(wanted || 1));
                                  }}
                                  title={showStockPane
                                    ? `SPH ${sphKey} / CYL ${formatCylLabel(cyl)} — ${inStock} in stock. Click to set how many to order.`
                                    : `SPH ${sphKey} / CYL ${formatCylLabel(cyl)} — click to set how many to order.`}
                                >
                                  {wanted || ''}
                                </OrderCellButton>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </StockTable>
            </TableWrapper>
          </PairPane>
        )}
      </PairLayout>

    </MatrixCard>
  );
};

// refreshKey lets a parent screen's Refresh button reach this page's own data.
// Embedded in the central dashboard this fetched only on mount, so Refresh on
// the Lens Stock tab did nothing.
const LensStockPage = ({ refreshKey = 0 }) => {
  const { t } = useTranslation();
  // The lens list is derived from the admin catalog: one entry per lens marked
  // "Stock Int", grouped by (real name, index). There is no free-text create —
  // marking a lens Stock Int in Admin → Lens Catalog is what puts it here.
  const [groups, setGroups] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [locations, setLocations] = useState({ stores: [] });
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationType, setLocationType] = useState('STORE');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [notice, setNotice] = useState(null);
  const [sphFilter, setSphFilter] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('replace');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const noticeTimer = useRef(null);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
  }, []);

  // Advisors see what an admin sees; they can change it only when the admin
  // put "Lens stock" on Edit for them (the server enforces it too).
  const advisor = advisorAccessFor(user);
  const isAdmin = user.role === 'admin' || !!advisor;
  const isCentralViewer = ['inventory_manager', 'operations_manager'].includes(user.role);
  const canManage = (isAdmin || isCentralViewer) && (!advisor || advisor['lens-stock'] === 'e');
  const isStoreManager = user.role === 'store_manager';
  const userStoreId = user.store?.id || user.store?._id || (typeof user.store === 'string' ? user.store : '') || user.storeId || '';
  const selectedStore = locations.stores.find((store) => String(store._id || store.id) === String(selectedLocationId));
  const isViewingCentralStore = !!selectedStore?.isCentralWarehouse || (isCentralViewer && String(selectedLocationId) === String(userStoreId));
  const canEditMatrix = canManage && isViewingCentralStore;
  // Ordering from central only makes sense when looking at a store's shelf, and
  // the request is raised against the caller's own store server-side.
  const canOrderFromCentral = isStoreManager && !isViewingCentralStore;
  const isReadOnly = !canEditMatrix;

  // A store orders against a blank copy of central's Stock Int tables: same
  // lenses, same SPH/CYL grid, no quantities on it. Its own shelf is still one
  // click away — that pane is also where Return to central lives.
  const [showOwnStock, setShowOwnStock] = useState(false);
  const showStockPane = !canOrderFromCentral || showOwnStock;

  // Stats are aggregated from every rendered lens, each of which reports its own
  // totals. Keyed by product so a lens that is filtered out or deleted drops out
  // of the totals instead of lingering.
  const [productStats, setProductStats] = useState({});

  const handleProductStats = useCallback((productId, values) => {
    setProductStats((prev) => {
      const existing = prev[productId];
      if (existing
        && existing.available === values.available
        && existing.reserved === values.reserved
        && existing.low === values.low
        && existing.filled === values.filled) {
        return prev;   // identical — don't re-render the page on every refetch
      }
      return { ...prev, [productId]: values };
    });
  }, []);

  // Only groups the server managed to bind to a lens_products row can hold
  // stock; an unbound one would have nothing to write against.
  const usableGroups = useMemo(
    () => groups.filter((g) => g.product && (g.product._id || g.product.id)),
    [groups]
  );

  // The lens picker filters the grid rather than choosing the one matrix:
  // empty shows every lens, side by side.
  const shownGroups = useMemo(() => (
    selectedProduct
      ? usableGroups.filter((g) => (g.product._id || g.product.id) === selectedProduct)
      : usableGroups
  ), [usableGroups, selectedProduct]);

  // Summed across every lens currently on screen, from what each card reports.
  const stats = useMemo(() => {
    const shownIds = new Set(shownGroups.map((g) => g.product._id || g.product.id));
    let available = 0; let reserved = 0; let low = 0; let filled = 0;
    Object.entries(productStats).forEach(([productId, v]) => {
      if (!shownIds.has(productId)) return;
      available += v.available; reserved += v.reserved; low += v.low; filled += v.filled;
    });
    return { products: shownGroups.length, available, reserved, low, filled };
  }, [shownGroups, productStats]);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    if (type === 'success') {
      noticeTimer.current = setTimeout(() => setNotice(null), 4000);
    }
  }, []);

  const reloadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const data = await getStockIntGroups();
      setGroups(data.groups || []);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Failed to load lenses from the catalog');
    } finally {
      setProductsLoading(false);
    }
  }, [showNotice]);

  useEffect(() => { reloadProducts(); }, [reloadProducts, refreshKey]);

  useEffect(() => {
    if (!isAdmin && !isCentralViewer) {
      if (['store_manager', 'inventory_manager', 'operations_manager', 'courier'].includes(user.role)) {
        setLocationType('STORE');
        setSelectedLocationId(userStoreId);
      }
      return;
    }

    const loadLocations = async () => {
      setLocationsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get('/api/locations', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setLocations({
          stores: data.stores || [],
        });
      } catch (error) {
        showNotice('error', error.response?.data?.message || 'Failed to load locations');
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, [isAdmin, isCentralViewer, showNotice, user.role, userStoreId]);

  useEffect(() => {
    if ((!isAdmin && !isCentralViewer) || locationsLoading || selectedLocationId || locationType !== 'STORE') return;
    const central = locations.stores.find((store) => store.isCentralWarehouse);
    const fallback = central || locations.stores[0];
    const id = fallback?._id || fallback?.id;
    if (id) setSelectedLocationId(id);
  }, [isAdmin, isCentralViewer, locationsLoading, locationType, locations.stores, selectedLocationId]);

  // A lens that stops being Stock Int drops out of the list, so a stale
  // selection would show an empty grid. Clear it instead.
  useEffect(() => {
    if (!selectedProduct) return;
    const stillThere = usableGroups.some((g) => (g.product._id || g.product.id) === selectedProduct);
    if (!stillThere) setSelectedProduct('');
  }, [usableGroups, selectedProduct]);

  const handleImport = async () => {
    if (!importFile) {
      showNotice('error', 'Select an Excel file first');
      return;
    }
    if (!selectedLocationId) {
      showNotice('error', 'Select a location first');
      return;
    }
    setImporting(true);
    setImportSummary(null);
    try {
      const result = await importExcel({
        file: importFile,
        location: { type: locationType, id: selectedLocationId },
        mode: importMode,
      });
      setImportSummary(result);
      await reloadProducts();
      if (result.productId) setSelectedProduct(result.productId);
      showNotice('success', `Import completed: ${result.createdCellsCount} created, ${result.updatedCellsCount} updated`);
    } catch (error) {
      showNotice('error', error.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };


  return (
    <Container>
      <Header>
        <div>
          <PageTitle>{t('lensStock.lensStockMatrix')}</PageTitle>
            <PageSubtitle>{canOrderFromCentral
              ? t('lensStock.theSameStockIntTables')
              : t('lensStock.everyLensMarkedStockInt')}</PageSubtitle>
        </div>
        <Toolbar>
          {/* Returning stock to central is done from the shelf pane, so the
              store needs a way back to it even though ordering is the default. */}
          {canOrderFromCentral && (
            <Button onClick={() => setShowOwnStock((prev) => !prev)}>
              {showOwnStock ? <EyeOff size={16} /> : <Eye size={16} />}
              {showOwnStock ? t('lensStock.hideMyStock') : t('lensStock.showMyStock')}
            </Button>
          )}
          {canEditMatrix && (
            <Button onClick={() => setImportOpen((prev) => !prev)}>
              <Upload size={16} />
              {t('lensStock.importExcel')}
            </Button>
          )}
        </Toolbar>
      </Header>

      <HeroPanel>
        <HeroGrid>
          <Field>
            <HeroLabel>{t('lensStock.lensFromStockIntCatalog')}</HeroLabel>
            <HeroSelect
              value={selectedProduct}
              onChange={(event) => setSelectedProduct(event.target.value)}
              disabled={productsLoading || !usableGroups.length}
            >
              <option value="">
                {productsLoading
                  ? t('lensStock.loading2')
                  : usableGroups.length
                    ? `All lenses (${usableGroups.length})`
                    : t('lensStock.noStockIntLenses')}
              </option>
              {usableGroups.map((group) => (
                <option key={group.key} value={group.product._id || group.product.id}>{group.label}</option>
              ))}
            </HeroSelect>
          </Field>
          <Field>
            <HeroLabel>&nbsp;</HeroLabel>
            <PageSubtitle style={{ fontSize: '0.8rem', margin: 0, alignSelf: 'center' }}>
              {t('lensStock.thisListMirrorsAdminLens')} <strong>{t('lensStock.stockInt')}</strong> {t('lensStock.there')}
            </PageSubtitle>
          </Field>
          <HeroButton onClick={reloadProducts} disabled={productsLoading}>
            <RefreshCw size={16} />
            {productsLoading ? t('lensStock.loading2') : t('lensStock.refreshList')}
          </HeroButton>
        </HeroGrid>
      </HeroPanel>

      <LocationBar>
        {(isAdmin || isCentralViewer) ? (
          <>
            <Select value={locationType} onChange={(event) => { setLocationType(event.target.value); setSelectedLocationId(''); }}>
              <option value="STORE">{t('lensStock.store')}</option>
            </Select>
            <Select value={selectedLocationId} onChange={(event) => setSelectedLocationId(event.target.value)} disabled={locationsLoading}>
              <option value="">{locationsLoading ? t('lensStock.loading2') : t('lensStock.selectLocation')}</option>
              {locations.stores.map((location) => (
                <option key={location._id || location.id} value={location._id || location.id}>{location.name}</option>
              ))}
            </Select>
          </>
        ) : (
          <CurrentLocation>
            <MapPin size={15} />
            {user.store?.name || user.store?.storeCode || 'Assigned Store'}
          </CurrentLocation>
        )}
      </LocationBar>


      <StatsGrid>
        <StatCard>
          <StatIcon $bg="#e0e7ff" $color="#4f46e5"><LayoutGrid size={21} /></StatIcon>
          <div><StatLabel>{t('lensStock.lenses')}</StatLabel><StatValue>{stats.products}</StatValue></div>
        </StatCard>
        {/* Quantity cards belong to the shelf view — in the order-only view
            there are no quantities on screen to summarise. */}
        {showStockPane && (
          <>
            <StatCard>
              <StatIcon $bg="#dcfce7" $color="#16a34a"><Box size={21} /></StatIcon>
              <div><StatLabel>{t('lensStock.available')}</StatLabel><StatValue>{stats.available.toLocaleString()}</StatValue></div>
            </StatCard>
            <StatCard>
              <StatIcon $bg="#fef3c7" $color="#d97706"><AlertTriangle size={21} /></StatIcon>
              <div><StatLabel>{t('lensStock.reserved')}</StatLabel><StatValue>{stats.reserved}</StatValue></div>
            </StatCard>
            <StatCard>
              <StatIcon $bg="#fee2e2" $color="#dc2626"><AlertCircle size={21} /></StatIcon>
              <div><StatLabel>{t('lensStock.lowCells')}</StatLabel><StatValue>{stats.low}</StatValue></div>
            </StatCard>
          </>
        )}
      </StatsGrid>

      {notice && (
        <Notice $type={notice.type}>
          {notice.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          <span>{notice.message}</span>
          <button onClick={() => setNotice(null)}><X size={14} /></button>
        </Notice>
      )}

      {canEditMatrix && (
        <ImportPanel $open={importOpen}>
          <ImportGrid>
            <Field>
              <Label>{t('lensStock.mode')}</Label>
              <Select value={importMode} onChange={(event) => setImportMode(event.target.value)}>
                <option value="replace">{t('lensStock.replaceValues')}</option>
                <option value="add">{t('lensStock.addToExisting')}</option>
              </Select>
            </Field>
            <Field>
              <Label>{t('lensStock.excelFile')}</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(event) => setImportFile(event.target.files?.[0] || null)} />
            </Field>
            <Button $variant="primary" onClick={handleImport} disabled={importing}>
              <Upload size={16} />
              {importing ? t('lensStock.importing') : t('lensStock.import')}
            </Button>
          </ImportGrid>
          {importSummary && (
            <PageSubtitle style={{ marginTop: '0.7rem' }}>
              {t('lensStock.created')} {importSummary.createdCellsCount || 0} {t('lensStock.cellsUpdated')} {importSummary.updatedCellsCount || 0} {t('lensStock.cells')}
            </PageSubtitle>
          )}
        </ImportPanel>
      )}

      {/* Applies to every matrix on screen, so one SPH lookup narrows all
          lenses at once instead of per card. */}
      {selectedLocationId && usableGroups.length > 0 && (
        <LocationBar>
          <SearchBox>
            <Search size={14} color="#94a3b8" />
            <input
              placeholder={t('lensStock.filterSph')}
              value={sphFilter}
              onChange={(event) => setSphFilter(event.target.value)}
            />
          </SearchBox>
          {sphFilter && (
            <Button onClick={() => setSphFilter('')}><X size={14} /> {t('lensStock.clear')}</Button>
          )}
          <CurrentLocation>
            {shownGroups.length} of {usableGroups.length} lens{usableGroups.length === 1 ? '' : 'es'} shown
          </CurrentLocation>
        </LocationBar>
      )}

      {!selectedLocationId ? (
        <EmptyState>
          <strong>{t('lensStock.chooseALocationFirst')}</strong>
          {t('lensStock.pickTheCentralWarehouseOr')}
        </EmptyState>
      ) : shownGroups.length === 0 ? (
        <EmptyState>
          <strong>
            {usableGroups.length
              ? t('lensStock.noLensMatchesThatFilter')
              : t('lensStock.noLensesAreMarkedStock')}
          </strong>
          {usableGroups.length
            ? t('lensStock.clearTheLensFilterTo')
            : t('lensStock.openAdminLensCatalogAnd')}
        </EmptyState>
      ) : (
        <MatrixGrid>
          {shownGroups.map((group) => (
            <ProductMatrix
              key={group.key}
              group={group}
              locationType={locationType}
              locationId={selectedLocationId}
              canEditMatrix={canEditMatrix}
              isReadOnly={isReadOnly}
              isStoreManager={isStoreManager}
              canOrderFromCentral={canOrderFromCentral}
              showStockPane={showStockPane}
              refreshKey={refreshKey}
              sphFilter={sphFilter}
              showNotice={showNotice}
              onStats={handleProductStats}
            />
          ))}
        </MatrixGrid>
      )}

      <Legend>
        <span><Dot $bg="#bbf7d0" $border="#86efac" />{t('lensStock.highMinusZone')}</span>
        <span><Dot $bg="#f5d0fe" $border="#e879f9" />{t('lensStock.middleMinusZone')}</span>
        <span><Dot $bg="#bae6fd" $border="#7dd3fc" />{t('lensStock.lowMinusZone')}</span>
        <span><Dot $bg="#e0e7ff" $border="#6366f1" />{t('lensStock.pendingEdit')}</span>
        <span><Dot $bg="white" $border="#f59e0b" />{t('lensStock.orangeUnderlineMeansReserved')}</span>
      </Legend>

    </Container>
  );
};

export default LensStockPage;
